import { fetchProgram as upstreamFetchProgram } from 'customer-flows/gql/queries/program'
import type IntlService from 'ember-intl/services/intl'
import { Settings as luxonSettings } from 'luxon'
import translationsDeDE from 'virtual:ember-intl/translations/de-de'
import translationsEsES from 'virtual:ember-intl/translations/es-es'
import translationsFrFR from 'virtual:ember-intl/translations/fr-fr'
import translationsItIT from 'virtual:ember-intl/translations/it-it'
import translationsPtPT from 'virtual:ember-intl/translations/pt-pt'
import translationsRuRU from 'virtual:ember-intl/translations/ru-ru'
import translationsZhCN from 'virtual:ember-intl/translations/zh-cn'
import { validateAndAddLanguageOverrides } from 'customer-flows/utils/validate-translation'

// maps gql program.local enum values to translation file locale codes
const LOCALE_MAP: Record<string, { locale: string; translations: Record<string, string> }> = {
  DE: { locale: 'de-de', translations: translationsDeDE },
  ES: { locale: 'es-es', translations: translationsEsES },
  FR: { locale: 'fr-fr', translations: translationsFrFR },
  IT: { locale: 'it-it', translations: translationsItIT },
  PT: { locale: 'pt-pt', translations: translationsPtPT },
  RU: { locale: 'ru-ru', translations: translationsRuRU },
  ZH_CN: { locale: 'zh-cn', translations: translationsZhCN }
}

function applyProgramLocale(intl: IntlService, apiLocale: string | undefined, languageOverrides: LanguageOverride[] | undefined) {
  const entry = LOCALE_MAP[apiLocale ?? '']
  const entryLocales = ['en-us']

  if (entry) {
    intl.addTranslations(entry.locale, entry.translations)
    entryLocales.unshift(entry.locale)
  }

  intl.setLocale(entryLocales)
  luxonSettings.defaultLocale = entry?.locale ?? 'en-us'

  validateAndAddLanguageOverrides(languageOverrides, intl, entry?.locale ?? 'en-us')
}

export default function program({ stores, callerContext, services }: CommandArgs) {
  const programStore: ProgramStore = stores['program']
  const applicationStore: ApplicationStore = stores['application']
  const intl: IntlService = services['intl']

  async function setProgram(program: ProgramType | Promise<ProgramType>) {
    programStore.program.promise = program

    await programStore.program.promise

    if (!programStore.initialLayoutName) {
      programStore.initialLayoutName = programStore.layoutName
    }

    applyProgramLocale(intl, programStore.locale, programStore.languageOverrides)
  }

  async function fetchProgram() {
    if (!applicationStore.legacyProgramId) {
      throw new Error('No program ID found in application store')
    }

    programStore.program.promise = upstreamFetchProgram(callerContext, { id: applicationStore.legacyProgramId }).then(
      (response) => response?.data?.program
    )

    await programStore.program.promise

    if (!programStore.initialLayoutName) {
      programStore.initialLayoutName = programStore.layoutName
    }

    applyProgramLocale(intl, programStore.locale, programStore.languageOverrides)

    return programStore.program.value
  }

  return { fetchProgram, setProgram }
}

declare global {
  export type ProgramCommands = ReturnType<typeof program>

  // Add to global scope
  interface CommandRegistry {
    program: ProgramCommands
  }
}
